import { ChangeEvent, KeyboardEvent, useEffect, useState } from 'react'
import { Transition } from '@headlessui/react'
import { useStripe } from '@stripe/react-stripe-js'
import { StripeCardElement } from '@stripe/stripe-js'
import PaymentForm from 'components/BookingPageComponents/PaymentForm'
import Translation from 'components/Translation'
import { useAppContext } from 'context/AppContext'
import {
    bookForClassUsingFreeAppointment,
    bookForClassUsingMembership,
    BookingPaymentDto,
    payAndBookForAppointment,
    payAndBookForClass,
} from 'services/booking'
import { BookingForm } from 'types/booking'
import { DiscountType } from 'types/discount'
import FreeAppointmentToggle from 'components/BookingPageComponents/FreeAppointmentToggle'
import { toHHMM12, toYmd } from 'utils/string-localization-helper'
import { checkCouponAvailability } from 'services/coupons'
import AppointmentPurchaseContainer, { useAppointmentContext } from 'context/appointmentPurchaseContext/AppointmentPurchaseContainer'
import { bookingAppointmentUsingCoupon } from 'services/appointment'
import { useTenantContext } from 'context/TenantContext'
import InputSpinner from 'components/Spinners/input'
import { EMAIL_REGEX, WORD_SINGLE_LINE_REGEX, PHONE_REGEX } from 'types/common'
import { classNames } from 'utils/dom-helpers'

export type CreateNewClassClientProps = {
    title: string
    data: BookingForm
    show: boolean
    onSuccess(booking: unknown): void
    toggleShow(b: boolean): void
}
export default function CreateNewClient({
    title,
    show,
    data,
    onSuccess,
    toggleShow,
}: CreateNewClassClientProps) {
    const stripe = useStripe()
    const { translations, loadComponentTranslations, is_mobile } = useAppContext()
    const { tenant } = useTenantContext()
    const { totalPrice } = useAppointmentContext()
    const [booking_data, setBookingData] = useState<
        BookingForm & {
            appointment_id?: string
            availability_id?: string
            end?: Date
            start?: Date
        }
    >({
        ...data,
        amount: data?.price,
    })

    const [card, setCard] = useState<StripeCardElement>()
    const [is_submitting, toggleFormSubmitStatus] = useState<boolean>(false)
    const [error_message, setErrorMessage] = useState<string>('')
    const [error_messages, setErrorMessages] = useState<string[]>([])
    const [is_free, setAsFree] = useState<boolean>()
    const [show_cardholder_permission, setShowCardholderPermission] =
        useState(false)
    const [cardholder_agree, setCardholderAgree] = useState(false)

    useEffect(() => {
        if (!translations.CALENDAR_TRANSLATION_ID) {
            loadComponentTranslations('CALENDAR_TRANSLATION_ID')
        }
    }, [translations])

    // Reset
    useEffect(() => {
        if (!show) {
            setAsFree(false)
            setErrorMessage('')
            setErrorMessages([])
            setShowCardholderPermission(false)
            toggleFormSubmitStatus(false)
        }
    }, [show])

    async function createPaymentMethod(): Promise<string | void> {
        if (!stripe || !data.connected_account_id) {
            setErrorMessage('no_payment_processor_integration')
            return
        }
        if (!card) return
        const result = await stripe.createPaymentMethod({
            type: 'card',
            card,
        })
        if (result.error) {
            setErrorMessage(result.error.code || 'card_error')
            return
        }
        return result.paymentMethod.id
    }

    async function createAndBook() {
        toggleFormSubmitStatus(true)
        setErrorMessages([])
        const field_errors: string[] = []
        if (!booking_data?.first_name) {
            field_errors.push('first_name_required')
        }
        if (!booking_data?.last_name) {
            field_errors.push('last_name_required')
        }
        if (!booking_data?.email) {
            field_errors.push('email_required')
        }
        if (!booking_data?.phone) {
            field_errors.push('valid_phone_required')
        }

        let coupon_id = ''
        if (
            booking_data.coupon_code &&
            booking_data.service_id &&
            booking_data.email
        ) {
            const coupon = await checkCouponAvailability({
                coupon_code: booking_data.coupon_code,
                service_id: booking_data.service_id,
                email: booking_data.email,
            })
            if (
                DiscountType.PERCENT === coupon.discountType &&
                coupon.discountValue === 100
            ) {
                coupon_id = coupon.id
            }
        }

        if (show_cardholder_permission && !card) {
            field_errors.push('credit_card_required')
        }
        if (field_errors.length) {
            if (field_errors.length > 2)
                setErrorMessage('please_provide_required_fields')
            else setErrorMessages(field_errors)
        }
        if (field_errors.length == 0 && booking_data && tenant?.id) {
            const {
                amount,
                availability_id,
                appointment_id,
                coupon_code,
                first_name,
                last_name,
                email,
                location_id,
                phone,
                service_id,
            } = booking_data

            let payment_method_id

            if (
                (availability_id || appointment_id) &&
                first_name &&
                last_name &&
                email &&
                phone &&
                service_id
            ) {
                if (!coupon_id && amount && !is_free) {
                    payment_method_id = await createPaymentMethod()
                }

                const dto: BookingPaymentDto = {
                    amount: amount as number,
                    paymentMethodId: payment_method_id as string,
                    clientEmail: email,
                    clientFirstName: first_name,
                    clientLastName: last_name,
                    clientPhone: phone,
                    tenantId: tenant.id,
                    locationId: location_id || '',
                    couponCode: coupon_code,
                }

                let booking, error_message
                if (coupon_id) {
                    if (availability_id) {
                        booking = await bookingAppointmentUsingCoupon({
                            ...dto,
                            order: {
                                clientEmail: email,
                                clientFirstName: first_name,
                                clientLastName: last_name,
                                clientPhone: phone,
                                tenantId: tenant.id,
                                type: 'SERVICE',
                                couponCode: coupon_code,
                                fullyPaid: false,
                            },
                            booking: {
                                groupClassAvailabilityId: availability_id,
                                clientEmail: email,
                                tenantId: tenant.id,
                            },
                            amount: totalPrice
                        })
                        
                        error_message = booking.message
                    }
                } else if (
                    amount &&
                    payment_method_id &&
                    data.connected_account_id &&
                    !is_free
                ) {
                    if (availability_id) {
                        dto.groupClassAvailabilityId = availability_id
                        booking = await payAndBookForClass(dto)
                    } else if (
                        booking_data.start &&
                        booking_data.end &&
                        payment_method_id
                    ) {
                        booking = await payAndBookForAppointment(
                            amount,
                            payment_method_id,
                            email,
                            first_name,
                            last_name,
                            phone,
                            tenant.id,
                            location_id || '',
                            {
                                date: toYmd(booking_data.start),
                                startTime: toHHMM12(booking_data.start),
                                endTime: toHHMM12(booking_data.end),
                                serviceId: booking_data.service_id,
                                locationId: location_id,
                            }
                        )
                    }
                    error_message = booking.message
                } else if (is_free) {
                    if (availability_id) {
                        booking = await bookForClassUsingFreeAppointment(
                            '',
                            email,
                            first_name,
                            last_name,
                            phone,
                            tenant.id,
                            availability_id
                        )
                    }
                    error_message = booking.message
                } else {
                    if (availability_id) {
                        booking = await bookForClassUsingMembership(
                            booking_data?.membership?.id || "",
                            email,
                            first_name,
                            last_name,
                            phone,
                            tenant.id,
                            availability_id,
                            false
                        )
                    }
                    error_message = booking.message
                }

                if (error_message) {
                    setErrorMessage(error_message)
                    setShowCardholderPermission(false)
                } else {
                    delete booking_data.email
                    delete booking_data.first_name
                    delete booking_data.last_name
                    delete booking_data.phone
                    setBookingData({
                        ...booking_data,
                    })
                    onSuccess({
                        ...booking,
                        client: {
                            first_name,
                            last_name,
                        },
                    })
                }
            }
        }

        toggleFormSubmitStatus(false)
    }

    function handleInputChange(e: ChangeEvent<HTMLInputElement>) {
        setErrorMessage('')
        setErrorMessages([])
        toggleFormSubmitStatus(false)
        setBookingData({
            ...booking_data,
            [e.target.name]: e.target.value,
        })
    }

    function handleEnterKey(e: KeyboardEvent<HTMLInputElement>) {
        if (e.key == 'Enter') createAndBook()
    }

    const getLocalStorageValue = () => {
        JSON.parse(localStorage.getItem('payment_method') as any) == 'CARD'
            ? (setShowCardholderPermission(true), setCardholderAgree(false))
            : (setShowCardholderPermission(false),
              setCardholderAgree(false))
    }

    return (
        <Transition
            show={show}
            enter="transition-all duration-300"
            enterFrom="opacity-0 -translate-y-full"
            enterTo="opacity-100 translate-y-0"
            leave="transition-all duration-300"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-full"
            className={classNames("flex flex-col h-screen ", is_mobile ? "w-screen relative" : "justify-center")}
        >
            <div
                className={classNames('px-6 pb-8 bg-white rounded-lg', is_mobile ? "absolute w-full" : "w-auto  mx-auto")}
            >
                <h3
                    className={`${
                        error_message || error_messages.length > 0
                            ? 'bg-red-200 bg-opacity-50 text-red-700'
                            : 'bg-primary-light bg-opacity-5 text-primary'
                    } font-semibold mb-4 leading-none whitespace-pre-line -mx-6 px-6 py-4 flex justify-between items-center text-lg`}
                >
                    <Translation content_key={title} />
                    <button
                        type="button"
                        onClick={() => {
                            toggleShow(false)
                        }}
                    >
                        <i className="feather-x text-xl" />
                    </button>
                </h3>
                <h5 className="text-xl font-semibold mt-6">
                    <Translation content_key="basic_info_panel_title" />
                </h5>
                {error_message ? (
                    <div className="bg-red-200 text-red-700 bg-opacity-30 p-4 mt-3 mb-6 rounded-lg">
                        <Translation content_key={error_message} />
                    </div>
                ) : (
                    ''
                )}
                {error_messages.length ? (
                    <div className="bg-red-200 text-red-700 bg-opacity-30 p-4 mt-3 mb-6 rounded-lg">
                        {error_messages.map((msg: string, idx: number) => (
                            <div key={idx}>
                                {<Translation content_key={msg} />}
                            </div>
                        ))}
                    </div>
                ) : (
                    ''
                )}
                <div className="flex gap-4 mt-6">
                    <fieldset className="w-1/2">
                        <label htmlFor="txt_first_name">
                            <Translation content_key="first_name" />
                            <sup className="text-red-700">*</sup>
                        </label>
                        <input
                            className="w-full rounded-lg border border-gray-300 placeholder-gray-300 mt-1"
                            type="text"
                            id="txt_first_name"
                            name="first_name"
                            onChange={handleInputChange}
                            onKeyPress={handleEnterKey}
                            pattern={WORD_SINGLE_LINE_REGEX.source}
                        />
                    </fieldset>
                    <fieldset className="w-1/2">
                        <label htmlFor="txt_last_name">
                            <Translation content_key="last_name" />
                            <sup className="text-red-700">*</sup>
                        </label>
                        <input
                            className="w-full rounded-lg border border-gray-300 placeholder-gray-300 mt-1"
                            type="text"
                            id="txt_last_name"
                            name="last_name"
                            onChange={handleInputChange}
                            onKeyPress={handleEnterKey}
                            pattern={WORD_SINGLE_LINE_REGEX.source}
                        />
                    </fieldset>
                </div>
                <div className="flex gap-4 mt-6">
                    <fieldset className="w-1/2">
                        <label htmlFor="txt_email">
                            <Translation content_key="email" />
                            <sup className="text-red-700">*</sup>
                        </label>
                        <input
                            className="w-full rounded-lg border border-gray-300 placeholder-gray-300 mt-1"
                            type="email"
                            id="txt_email"
                            name="email"
                            onChange={handleInputChange}
                            onKeyPress={handleEnterKey}
                            pattern={EMAIL_REGEX.source}
                        />
                    </fieldset>
                    <fieldset className="w-1/2">
                        <label htmlFor="txt_phone">
                            <Translation content_key="phone" />
                            <sup className="text-red-700">*</sup>
                        </label>
                        <input
                            className="w-full rounded-lg border border-gray-300 placeholder-gray-300 mt-1"
                            type="text"
                            id="txt_phone"
                            name="phone"
                            onChange={handleInputChange}
                            onKeyPress={handleEnterKey}
                            pattern={PHONE_REGEX.source}
                        />
                    </fieldset>
                </div>

                <FreeAppointmentToggle
                    onChange={(val: boolean) => {
                        setAsFree(val)
                        setShowCardholderPermission(false)
                    }}
                    value={is_free}
                />

                {!is_free ? (
                    <>
                        <AppointmentPurchaseContainer>
                            <PaymentForm
                                booking_data={booking_data}
                                setBookingData={setBookingData}
                                classnames="mt-5"
                                setCard={(card: StripeCardElement) => {
                                    setErrorMessage('')
                                    setCard(card)
                                }}
                                handleSubmit={createAndBook}
                                label="customer_will_be_billed"
                                getLocalStorageValue={getLocalStorageValue}
                            />
                        </AppointmentPurchaseContainer>

                        {show_cardholder_permission && (
                            <div className="flex items-center px-0 mb-12">
                                <input
                                    type="checkbox"
                                    checked={cardholder_agree}
                                    className="h-5 w-5  border-gray-300 rounded text-primary focus:ring-primary-light checked:bg-primary-light"
                                    onChange={(e) =>
                                        setCardholderAgree(e.target.checked)
                                    }
                                />
                                <span className="ml-2 text-sm">
                                    <Translation content_key="add_attendee_description" />
                                </span>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="my-8" />
                )}

                {/* Buttons */}
                <div className="flex items-center justify-end gap-4 brdr-top">
                    <button
                        type="button"
                        className="disabled:border-gray-100 disabled:text-gray-200 border border-gray-200 rounded-lg aot-btn"
                        onClick={() => {
                            toggleShow(false)
                        }}
                        disabled={is_submitting}
                    >
                        <Translation content_key="cancel" />
                    </button>
                    <button
                        type="button"
                        className="disabled:bg-gray-300 bg-primary text-white rounded-lg aot-btn"
                        onClick={createAndBook}
                        disabled={
                            (show_cardholder_permission &&
                                !cardholder_agree) ||
                            is_submitting
                        }
                    >
                        <InputSpinner loading={is_submitting} />
                        <Translation content_key="add_and_book_attendee" />
                    </button>
                </div>
            </div>
        </Transition>
    )
}
